// src/theme/themes.ts
export type ThemeName =
  | "light"
  | "dark"
  | "nature"
  | "pastel"
  | "vintage"
  | "nordic"
  | "nordicDark"
  | "neoDark";

export const themes: Record<ThemeName, Record<string, string>> = {
  light: {
    /* ===== App Shell ===== */
    "--app-bg": "#f9fafb", // gray-50
    "--app-text": "#111827",
    "--brand-gradient-from": "#2563eb",
    "--brand-gradient-to": "#3b82f6",
    "--brand-shadow": "0 6px 18px rgba(37, 99, 235, 0.25)",
    "--brand-text": "#ffffff",
    "--brand-badge-bg": "#ffffff",
    "--brand-badge-text": "#2563eb",
    "--menu-button-text": "#1f2937",
    "--menu-button-hover": "rgba(0,0,0,0.04)",

    /* ===== Base Page ===== */
    "--page-bg": "#f9fafb",
    "--page-surface-bg": "#ffffff",
    "--page-surface-border": "#e5e7eb",
    "--page-surface-shadow": "0 1px 3px rgba(0,0,0,0.08)",
    "--page-title": "#111827",
    "--page-text": "#374151",

    /* ===== Project Header ===== */
    "--header-bg": "#ffffff",
    "--header-border": "#d1d5db" /* == --stroke */,
    "--header-text": "#2563eb" /* == --main-text-color */,
    "--header-icon": "#2563eb",
    "--header-icon-hover": "rgba(37,99,235,0.08)",
    "--header-menu-bg": "#ffffff",
    "--header-menu-border": "#e5e7eb",
    "--header-menu-item-hover": "#f3f4f6",
    "--header-divider": "#e5e7eb",

    /* ===== Project Dash Card ===== */
    "--card-gradient-from": "#3b82f6", // blue-500
    "--card-gradient-to": "#7c3aed", // purple-600
    "--card-text": "#ffffff",
    "--card-subtext": "#dbeafe",
    "--card-glass-bg": "rgba(255,255,255,0.2)",
    "--card-glass-border": "rgba(255,255,255,0.3)",
    "--card-bubble": "rgba(255,255,255,0.1)",

    /* ===== Model Dash Card ===== */
    "--model-gradient-from": "#10b981", // emerald-500
    "--model-gradient-to": "#0d9488", // teal-600
    "--model-text": "#ffffff",
    "--model-subtext": "#d1fae5",
    "--model-glass-bg": "rgba(255,255,255,0.2)",
    "--model-glass-border": "rgba(255,255,255,0.3)",
    "--model-progress-track": "rgba(255,255,255,0.25)",
    "--model-progress-bar": "#ffffff",
    "--model-bubble": "rgba(255,255,255,0.1)",

    /* ===== Card Component ===== */
    "--card-bg": "#ffffff",
    "--card-border": "#e5e7eb",
    "--card-icon-bg": "#eff6ff",
    "--card-icon": "#2563eb",
    "--card-title": "#111827",
    "--card-action-hover": "#f3f4f6",
    "--card-action-icon": "#4b5563",
    "--card-hover-bg": "#f9fafb", // 살짝 밝아짐
    "--card-hover-border": "#d1d5db",
    "--card-hover-shadow": "0 6px 16px rgba(0,0,0,0.08)",

    /* ===== Custome Button ===== */
    "--contained-bg": "#2563eb",
    "--contained-text": "#ffffff",
    "--contained-bg-hover": "#1d4ed8",
    "--contained-bg-active": "#1e40af",
    "--contained-focus-ring": "rgba(37,99,235,0.35)",
    "--contained-disabled-bg": "#dbeafe",
    "--contained-disabled-text": "#93c5fd",
    "--outlined-bg": "#ffffff",
    "--outlined-border": "#d1d5db",
    "--outlined-text": "#2563eb",
    "--outlined-bg-hover": "#eff6ff",
    "--outlined-text-hover": "#1d4ed8",
    "--outlined-bg-active": "#dbeafe",
    "--outlined-focus-ring": "rgba(37,99,235,0.25)",
    "--outlined-disabled-bg": "#f3f4f6",
    "--outlined-disabled-text": "#9ca3af",

    /* ===== Badge ===== */
    "--badge-analysis-bg": "#DBEAFE",
    "--badge-analysis-text": "#1D4ED8",
    "--badge-analysis-border": "#BFDBFE",
    "--badge-inspection-bg": "#DCFCE7",
    "--badge-inspection-text": "#15803D",
    "--badge-inspection-border": "#BBF7D0",
    "--badge-classification-bg": "#FFEDD5",
    "--badge-classification-text": "#C2410C",
    "--badge-classification-border": "#FED7AA",

    /* ===== Image Table ===== */
    "--table-bg": "#FFFFFF",
    "--table-header-bg": "#F8FAFC",
    "--table-header-text": "#1F2937",
    "--table-header-border": "#E5E7EB",
    "--table-row-bg": "#FFFFFF",
    "--table-row-hover-bg": "#F1F5F9",
    "--table-row-selected-bg": "#DBEAFE",
    "--table-text": "#1F2937",
    "--table-border": "#E5E7EB",
    "--table-divider": "#E5E7EB",
    "--table-text-highlight": "#2563EB",

    /* ===== Table Meta ===== */
    "--table-meta-bg": "#ffffff",
    "--table-meta-text": "#374151",
    "--table-meta-border": "#E5E7EB",
  },

  dark: {
    /* ===== App Shell ===== */
    "--app-bg": "#020617", // --page-bg와 같게: 사이드 영역과 본문 경계가 보이지 않도록
    "--app-text": "#e5e7eb",
    "--brand-gradient-from": "#020617",
    "--brand-gradient-to": "#1e293b",
    "--brand-shadow": "0 6px 18px rgba(0,0,0,0.6)",
    "--brand-text": "#e5e7eb",
    "--brand-badge-bg": "#1e293b",
    "--brand-badge-text": "#93c5fd",
    "--menu-button-text": "#e5e7eb",
    "--menu-button-hover": "rgba(255,255,255,0.08)",

    /* ===== Base Page ===== */
    "--page-bg": "#020617",
    "--page-surface-bg": "#020b1a",
    "--page-surface-border": "#1e293b",
    "--page-surface-shadow": "0 1px 4px rgba(0,0,0,0.6)",
    "--page-title": "#e5e7eb",
    "--page-text": "#cbd5e1",

    /* ===== Project Header ===== */
    "--header-bg": "#111827",
    "--header-border": "#374151",
    "--header-text": "#e5e7eb",
    "--header-icon": "#9ca3af",
    "--header-icon-hover": "rgba(255,255,255,0.08)",
    "--header-menu-bg": "#1f2933",
    "--header-menu-border": "#374151",
    "--header-menu-item-hover": "#374151",
    "--header-divider": "#374151",

    /* ===== Project Dash Card ===== */
    "--card-gradient-from": "#020617", // slate-950
    "--card-gradient-to": "#1e293b", // slate-800
    "--card-text": "#e5e7eb",
    "--card-subtext": "#94a3b8",
    "--card-glass-bg": "rgba(255,255,255,0.08)",
    "--card-glass-border": "rgba(255,255,255,0.15)",
    "--card-bubble": "rgba(255,255,255,0.06)",

    /* ===== Model Dash Card ===== */
    "--model-gradient-from": "#022c22",
    "--model-gradient-to": "#134e4a",
    "--model-text": "#e5e7eb",
    "--model-subtext": "#99f6e4",
    "--model-glass-bg": "rgba(255,255,255,0.08)",
    "--model-glass-border": "rgba(255,255,255,0.15)",
    "--model-progress-track": "rgba(255,255,255,0.15)",
    "--model-progress-bar": "#5eead4",
    "--model-bubble": "rgba(255,255,255,0.06)",

    /* ===== Card Component ===== */
    "--card-bg": "#020617",
    "--card-border": "#1e293b",
    "--card-icon-bg": "#041127",
    "--card-icon": "#60a5fa",
    "--card-title": "#e5e7eb",
    "--card-action-hover": "rgba(255,255,255,0.08)",
    "--card-action-icon": "#cbd5f5",
    "--card-hover-bg": "#020617",
    "--card-hover-border": "#334155",
    "--card-hover-shadow": "0 6px 16px rgba(0,0,0,0.6)",

    /* ===== Custome Button ===== */
    "--contained-bg": "#374151",
    "--contained-text": "#ffffff",
    "--contained-bg-hover": "#4b5563",
    "--contained-bg-active": "#1f2937",
    "--contained-focus-ring": "rgba(255,255,255,0.25)",
    "--contained-disabled-bg": "#1f2937",
    "--contained-disabled-text": "#6b7280",
    "--outlined-bg": "#111827",
    "--outlined-border": "#374151",
    "--outlined-text": "#e5e7eb",
    "--outlined-bg-hover": "#1f2937",
    "--outlined-text-hover": "#ffffff",
    "--outlined-bg-active": "#374151",
    "--outlined-focus-ring": "rgba(255,255,255,0.2)",
    "--outlined-disabled-bg": "#111827",
    "--outlined-disabled-text": "#6b7280",

    /* ===== Badge ===== */
    "--badge-analysis-bg": "rgba(29,78,216,0.25)",
    "--badge-analysis-text": "#93C5FD",
    "--badge-analysis-border": "#1E40AF",
    "--badge-inspection-bg": "rgba(21,128,61,0.25)",
    "--badge-inspection-text": "#86EFAC",
    "--badge-inspection-border": "#166534",
    "--badge-classification-bg": "rgba(194,65,12,0.25)",
    "--badge-classification-text": "#FDBA74",
    "--badge-classification-border": "#9A3412",

    /* ===== Image Table ===== */
    "--table-bg": "#1F2937",
    "--table-header-bg": "#111827",
    "--table-header-text": "#E5E7EB",
    "--table-header-border": "#374151",
    "--table-row-bg": "#1F2937",
    "--table-row-hover-bg": "#374151",
    "--table-row-selected-bg": "#2563EB",
    "--table-text": "#E5E7EB",
    "--table-border": "#374151",
    "--table-divider": "#374151",
    "--table-text-highlight": "#60A5FA",

    /* ===== Table Meta ===== */
    "--table-meta-bg": "#0F172A",
    "--table-meta-text": "#E5E7EB",
    "--table-meta-border": "#1E293B",
  },

  nature: {
    /* ===== App Shell ===== */
    "--app-bg": "#F5F7F4",
    "--app-text": "#1F2A24",
    "--brand-gradient-from": "#3E5B52",
    "--brand-gradient-to": "#243E37",
    "--brand-shadow": "0 6px 18px rgba(36, 62, 55, 0.36)",
    "--brand-text": "#ffffff",
    "--brand-badge-bg": "#EEF2EF",
    "--brand-badge-text": "#4F6F64",
    "--menu-button-text": "#1F2A24",
    "--menu-button-hover": "rgba(0,0,0,0.04)",

    /* ===== Base Page ===== */
    "--page-bg": "#F5F7F4",
    "--page-surface-bg": "#FFFFFF",
    "--page-surface-border": "#D9DED9",
    "--page-surface-shadow": "0 1px 4px rgba(47, 79, 70, 0.18)",
    "--page-title": "#1F2A24",
    "--page-text": "#5F6E67",

    /* ===== Project Header ===== */
    "--header-bg": "#F5F7F4",
    "--header-border": "#D9DED9",
    "--header-text": "#1F2A24",
    "--header-icon": "#3E5B52",
    "--header-icon-hover": "rgba(62,91,82,0.14)",
    "--header-menu-bg": "#FFFFFF",
    "--header-menu-border": "#D9DED9",
    "--header-menu-item-hover": "#EEF2EF",
    "--header-divider": "#D9DED9",

    /* ===== Project Dash Card ===== */
    "--card-gradient-from": "#587E70",
    "--card-gradient-to": "#3E5B52",
    "--card-text": "#FFFFFF",
    "--card-subtext": "#E6ECE8",
    "--card-glass-bg": "rgba(255,255,255,0.22)",
    "--card-glass-border": "rgba(255,255,255,0.32)",
    "--card-bubble": "rgba(255,255,255,0.14)",

    /* ===== Model Dash Card ===== */
    "--model-gradient-from": "#3E5B52",
    "--model-gradient-to": "#243E37",
    "--model-text": "#FFFFFF",
    "--model-subtext": "#DCE7E2",
    "--model-glass-bg": "rgba(255,255,255,0.22)",
    "--model-glass-border": "rgba(255,255,255,0.32)",
    "--model-progress-track": "rgba(255,255,255,0.28)",
    "--model-progress-bar": "#FFFFFF",
    "--model-bubble": "rgba(255,255,255,0.14)",

    /* ===== Card Component ===== */
    "--card-bg": "#FFFFFF",
    "--card-border": "#D9DED9",
    "--card-icon-bg": "#EEF2EF",
    "--card-icon": "#3E5B52",
    "--card-title": "#1F2A24",
    "--card-action-hover": "#EEF2EF",
    "--card-action-icon": "#5F6E67",
    "--card-hover-bg": "#F1F4F2",
    "--card-hover-border": "#C9D0CB",
    "--card-hover-shadow": "0 6px 16px rgba(47, 79, 70, 0.22)",

    /* ===== Custom Button ===== */
    "--contained-bg": "#3E5B52",
    "--contained-text": "#FFFFFF",
    "--contained-bg-hover": "#354F47",
    "--contained-bg-active": "#2E463F",
    "--contained-focus-ring": "rgba(62,91,82,0.38)",
    "--contained-disabled-bg": "#E3E8E5",
    "--contained-disabled-text": "#9FB0A8",
    "--outlined-bg": "#FFFFFF",
    "--outlined-border": "#D9DED9",
    "--outlined-text": "#4F6F64",
    "--outlined-bg-hover": "#EEF2EF",
    "--outlined-text-hover": "#2F4F46",
    "--outlined-bg-active": "#E3E8E5",
    "--outlined-focus-ring": "rgba(79,111,100,0.25)",
    "--outlined-disabled-bg": "#F5F7F4",
    "--outlined-disabled-text": "#B0BDB7",

    /* ===== Badge ===== */
    "--badge-analysis-bg": "#ECFDF5",
    "--badge-analysis-text": "#047857",
    "--badge-analysis-border": "#A7F3D0",
    "--badge-inspection-bg": "#F0FDFA",
    "--badge-inspection-text": "#0F766E",
    "--badge-inspection-border": "#99F6E4",
    "--badge-classification-bg": "#F7FEE7",
    "--badge-classification-text": "#4D7C0F",
    "--badge-classification-border": "#D9F99D",

    /* ===== Image Table ===== */
    "--table-bg": "#FFFFFF",
    "--table-header-bg": "#F4F9F4",
    "--table-header-text": "#35503A",
    "--table-header-border": "#DDE8DD",
    "--table-row-bg": "#FFFFFF",
    "--table-row-hover-bg": "#F7FDF7",
    "--table-row-selected-bg": "#C9D8B6",
    "--table-text": "#35503A",
    "--table-border": "#E2E9E2",
    "--table-divider": "#E2E9E2",
    "--table-text-highlight": "#4A6B4A",

    /* ===== Table Meta ===== */
    "--table-meta-bg": "#F4F9F4",
    "--table-meta-text": "#35503A",
    "--table-meta-border": "#DDE8DD",
  },

  pastel: {
    /* ===== App Shell ===== */
    "--app-bg": "#F5F6FA", // 하얀색에서 약간 따뜻하게
    "--app-text": "#3F3D56",
    "--brand-gradient-from": "#D7E3F1", // 기존 하늘색
    "--brand-gradient-to": "#E1DAF0", // 연보라로 부드럽게
    "--brand-shadow": "0 6px 18px rgba(180, 180, 210, 0.45)", // 살짝 따뜻한 그림자
    "--brand-text": "#3F3D56",
    "--brand-badge-bg": "#F5F6FA",
    "--brand-badge-text": "#7A7693",
    "--menu-button-text": "#3F3D56",
    "--menu-button-hover": "rgba(0,0,0,0.04)",

    /* ===== Base Page ===== */
    "--page-bg": "#F5F6FA",
    "--page-surface-bg": "#FFFFFF",
    "--page-surface-border": "#D6D3E8",
    "--page-surface-shadow": "0 1px 4px rgba(180, 180, 210, 0.3)",
    "--page-title": "#2F2D42",
    "--page-text": "#5B5A72",

    /* ===== Project Header ===== */
    "--header-bg": "#F5F6FA",
    "--header-border": "#D6D3E8",
    "--header-text": "#3F3D56",
    "--header-icon": "#7A7693",
    "--header-icon-hover": "rgba(122,118,147,0.15)",
    "--header-menu-bg": "#FFFFFF",
    "--header-menu-border": "#D6D3E8",
    "--header-menu-item-hover": "#EDEAF6", // 연보라 톤으로 약간 따뜻하게
    "--header-divider": "#D6D3E8",

    /* ===== Project Dash Card ===== */
    "--card-gradient-from": "#D7E3F1",
    "--card-gradient-to": "#E8E1F5", // 연보라 강조
    "--card-text": "#2F2D42",
    "--card-subtext": "#5B5A72",
    "--card-glass-bg": "rgba(255,255,255,0.45)",
    "--card-glass-border": "rgba(255,255,255,0.6)",
    "--card-bubble": "rgba(255,255,255,0.3)",

    /* ===== Model Dash Card ===== */
    "--model-gradient-from": "#D7E3F1",
    "--model-gradient-to": "#EDE6F5", // 연보라 톤
    "--model-text": "#2F2D42",
    "--model-subtext": "#5B5A72",
    "--model-glass-bg": "rgba(255,255,255,0.5)",
    "--model-glass-border": "rgba(255,255,255,0.65)",
    "--model-progress-track": "rgba(255,255,255,0.55)",
    "--model-progress-bar": "#FFFFFF",
    "--model-bubble": "rgba(255,255,255,0.3)",

    /* ===== Card Component ===== */
    "--card-bg": "#FFFFFF",
    "--card-border": "#D6D3E8",
    "--card-icon-bg": "#F0F0FA", // 살짝 따뜻한 톤
    "--card-icon": "#7A7693",
    "--card-title": "#2F2D42",
    "--card-action-hover": "#EDEAF6",
    "--card-action-icon": "#5B5A72",
    "--card-hover-bg": "#F8F7FC",
    "--card-hover-border": "#DCD6EB",
    "--card-hover-shadow": "0 6px 16px rgba(180,180,210,0.35)",

    /* ===== Custom Button ===== */
    "--contained-bg": "#7A78B8",
    "--contained-text": "#FFFFFF",
    "--contained-bg-hover": "#6B69A8",
    "--contained-bg-active": "#5E5C96",
    "--contained-focus-ring": "rgba(122,120,184,0.35)",
    "--contained-disabled-bg": "#E6E7F2",
    "--contained-disabled-text": "#A8A7C7",
    "--outlined-bg": "#FFFFFF",
    "--outlined-border": "#CFCDE4",
    "--outlined-text": "#7A78B8",
    "--outlined-bg-hover": "#F2F1FA",
    "--outlined-text-hover": "#6B69A8",
    "--outlined-bg-active": "#E6E7F2",
    "--outlined-focus-ring": "rgba(122,120,184,0.25)",
    "--outlined-disabled-bg": "#FAFAFD",
    "--outlined-disabled-text": "#A8A7C7",

    /* ===== Badge ===== */
    "--badge-analysis-bg": "#E7EAF6",
    "--badge-analysis-text": "#4B4A8A",
    "--badge-analysis-border": "#D4D7EE",
    "--badge-inspection-bg": "#F3E6EB",
    "--badge-inspection-text": "#8A4B64",
    "--badge-inspection-border": "#E7CED8",
    "--badge-classification-bg": "#D7EAFB",
    "--badge-classification-text": "#3F6EB0",
    "--badge-classification-border": "#BFD9F4",

    /* ===== Image Table ===== */
    "--table-bg": "#FFFFFF",
    "--table-header-bg": "#EDEAF6",
    "--table-header-text": "#2F2D42",
    "--table-header-border": "#D6D3E8",
    "--table-row-bg": "#FFFFFF",
    "--table-row-hover-bg": "#F8F7FC",
    "--table-row-selected-bg": "#E7EAF6",
    "--table-text": "#2F2D42",
    "--table-border": "#D6D3E8",
    "--table-divider": "#E6E7F2",
    "--table-text-highlight": "#4B4A8A",

    /* ===== Table Meta ===== */
    "--table-meta-bg": "#FFFFFF",
    "--table-meta-text": "#3F3D56",
    "--table-meta-border": "#CBC6DA",
  },

  vintage: {
    /* ===== App Shell ===== */
    "--app-bg": "#F6F4EE",
    "--app-text": "#3E4A3C",
    "--brand-gradient-from": "#6B8E6E",
    "--brand-gradient-to": "#3E5F55",
    "--brand-shadow": "0 6px 18px rgba(62, 95, 85, 0.35)",
    "--brand-text": "#ffffff",
    "--brand-badge-bg": "#ffffff",
    "--brand-badge-text": "#6B8E6E",
    "--menu-button-text": "#3E4A3C",
    "--menu-button-hover": "rgba(0,0,0,0.04)",

    /* ===== Base Page ===== */
    "--page-bg": "#F6F4EE",
    "--page-surface-bg": "#FBFAF6",
    "--page-surface-border": "#C9C2A6",
    "--page-surface-shadow": "0 1px 4px rgba(62, 95, 85, 0.22)",
    "--page-title": "#3E4A3C",
    "--page-text": "#6B7268",

    /* ===== Project Header ===== */
    "--header-bg": "#F6F4EE",
    "--header-border": "#C9C2A6",
    "--header-text": "#3E4A3C",
    "--header-icon": "#6B8E6E",
    "--header-icon-hover": "rgba(107,142,110,0.15)",
    "--header-menu-bg": "#ffffff",
    "--header-menu-border": "#C9C2A6",
    "--header-menu-item-hover": "#EFECDD",
    "--header-divider": "#C9C2A6",

    /* ===== Project Dash Card ===== */
    "--card-gradient-from": "#D8D2B8",
    "--card-gradient-to": "#BFB79A",
    "--card-text": "#3E4A3C",
    "--card-subtext": "#6B7268",
    "--card-glass-bg": "rgba(255,255,255,0.4)",
    "--card-glass-border": "rgba(255,255,255,0.5)",
    "--card-bubble": "rgba(255,255,255,0.28)",

    /* ===== Model Dash Card ===== */
    "--model-gradient-from": "#6B8E6E",
    "--model-gradient-to": "#4F6F64",
    "--model-text": "#ffffff",
    "--model-subtext": "#E8F1EC",
    "--model-glass-bg": "rgba(255,255,255,0.25)",
    "--model-glass-border": "rgba(255,255,255,0.35)",
    "--model-progress-track": "rgba(255,255,255,0.3)",
    "--model-progress-bar": "#ffffff",
    "--model-bubble": "rgba(255,255,255,0.15)",

    /* ===== Card Component ===== */
    "--card-bg": "#FEFDF9",
    "--card-border": "#C9C2A6",
    "--card-icon-bg": "#BFB79A",
    "--card-icon": "#3E4A3C",
    "--card-title": "#3E4A3C",
    "--card-action-hover": "#EFECDD",
    "--card-action-icon": "#6B7268",
    "--card-hover-bg": "#F3F1E6",
    "--card-hover-border": "#B9B091",
    "--card-hover-shadow": "0 6px 16px rgba(62, 95, 85, 0.22)",

    /* ===== Custom Button ===== */
    "--contained-bg": "#6B8E6E",
    "--contained-text": "#ffffff",
    "--contained-bg-hover": "#5F8063",
    "--contained-bg-active": "#55765A",
    "--contained-focus-ring": "rgba(107,142,110,0.4)",
    "--contained-disabled-bg": "#E4ECE7",
    "--contained-disabled-text": "#9BB6A0",

    "--outlined-bg": "#ffffff",
    "--outlined-border": "#C9C2A6",
    "--outlined-text": "#3E4A3C",
    "--outlined-bg-hover": "#EFECDD",
    "--outlined-text-hover": "#3E4A3C",
    "--outlined-bg-active": "#E5E1CC",
    "--outlined-focus-ring": "rgba(201,194,166,0.35)",
    "--outlined-disabled-bg": "#FBFAF6",
    "--outlined-disabled-text": "#B1AA8F",

    /* ===== Badge ===== */
    "--badge-analysis-bg": "#F5F5F4",
    "--badge-analysis-text": "#57534E",
    "--badge-analysis-border": "#D6D3D1",
    "--badge-inspection-bg": "#ece0c4ff",
    "--badge-inspection-text": "#7C4A12",
    "--badge-inspection-border": "#dbc482",
    "--badge-classification-bg": "#edf3e0",
    "--badge-classification-text": "#3F4F3A",
    "--badge-classification-border": "#C9D2B8",

    /* ===== Image Table ===== */
    "--table-bg": "#F6F4EE",
    "--table-header-bg": "#E6DDC9",
    "--table-header-text": "#39463B",
    "--table-header-border": "#CBB78A",
    "--table-row-bg": "#F6F4EE",
    "--table-row-hover-bg": "#EDE4CF",
    "--table-row-selected-bg": "#D3B26F",
    "--table-text": "#353F36",
    "--table-border": "#CFC3A8",
    "--table-divider": "#DED3BA",
    "--table-text-highlight": "#6B7A4E",
  },

  nordic: {
    /* ===== App Shell ===== */
    "--app-bg": "#F6FAFA",
    "--app-text": "#0F172A",
    "--brand-gradient-from": "#004E64",
    "--brand-gradient-to": "#0A6E75",
    "--brand-shadow": "0 6px 18px rgba(0, 78, 100, 0.32)",
    "--brand-text": "#FFFFFF",
    "--brand-badge-bg": "#FFFFFF",
    "--brand-badge-text": "#004E64",
    "--menu-button-text": "#0F172A",
    "--menu-button-hover": "rgba(0,0,0,0.04)",

    /* ===== Base Page ===== */
    "--page-bg": "#F6FAFA",
    "--page-surface-bg": "#FFFFFF",
    "--page-surface-border": "#D1E5E1",
    "--page-surface-shadow": "0 1px 4px rgba(0, 78, 100, 0.16)",
    "--page-title": "#0F172A",
    "--page-text": "#475569",

    /* ===== Project Header ===== */
    "--header-bg": "#F6FAFA",
    "--header-border": "#D1E5E1",
    "--header-text": "#0F172A",
    "--header-icon": "#004E64",
    "--header-icon-hover": "rgba(0,78,100,0.12)",
    "--header-menu-bg": "#FFFFFF",
    "--header-menu-border": "#D1E5E1",
    "--header-menu-item-hover": "#EDF6F5",
    "--header-divider": "#D1E5E1",

    /* ===== Project Dash Card ===== */
    "--card-gradient-from": "#0A6E75",
    "--card-gradient-to": "#7FC6C1",
    "--card-text": "#0F172A",
    "--card-subtext": "#475569",
    "--card-glass-bg": "rgba(255,255,255,0.32)",
    "--card-glass-border": "rgba(255,255,255,0.42)",
    "--card-bubble": "rgba(255,255,255,0.22)",

    /* ===== Model Dash Card ===== */
    "--model-gradient-from": "#004E64",
    "--model-gradient-to": "#0F3A44",
    "--model-text": "#FFFFFF",
    "--model-subtext": "#DFF3F1",
    "--model-glass-bg": "rgba(255,255,255,0.22)",
    "--model-glass-border": "rgba(255,255,255,0.32)",
    "--model-progress-track": "rgba(255,255,255,0.28)",
    "--model-progress-bar": "#FFFFFF",
    "--model-bubble": "rgba(255,255,255,0.16)",

    /* ===== Card Component ===== */
    "--card-bg": "#FFFFFF",
    "--card-border": "#D1E5E1",
    "--card-icon-bg": "#EDF6F5",
    "--card-icon": "#004E64",
    "--card-title": "#0F172A",
    "--card-action-hover": "#F1F5F9",
    "--card-action-icon": "#475569",
    "--card-hover-bg": "#ECF7F5",
    "--card-hover-border": "#C5DEDA",
    "--card-hover-shadow": "0 6px 16px rgba(0, 78, 100, 0.18)",

    /* ===== Custom Button ===== */
    "--contained-bg": "#004E64",
    "--contained-text": "#FFFFFF",
    "--contained-bg-hover": "#0A6E75",
    "--contained-bg-active": "#003845",
    "--contained-focus-ring": "rgba(0,78,100,0.4)",
    "--contained-disabled-bg": "#E6F4F1",
    "--contained-disabled-text": "#9FCFCA",

    "--outlined-bg": "#FFFFFF",
    "--outlined-border": "#D1E5E1",
    "--outlined-text": "#004E64",
    "--outlined-bg-hover": "#EDF6F5",
    "--outlined-text-hover": "#004E64",
    "--outlined-bg-active": "#DFF3F1",
    "--outlined-focus-ring": "rgba(0,78,100,0.25)",
    "--outlined-disabled-bg": "#F6FAFA",
    "--outlined-disabled-text": "#B6DAD6",

    /* ===== Badge ===== */
    "--badge-analysis-bg": "#E0F2FE",
    "--badge-analysis-text": "#075985",
    "--badge-analysis-border": "#BAE6FD",
    "--badge-inspection-bg": "#E6FFFA",
    "--badge-inspection-text": "#0F766E",
    "--badge-inspection-border": "#99F6E4",
    "--badge-classification-bg": "#ECFEFF",
    "--badge-classification-text": "#155E75",
    "--badge-classification-border": "#A5F3FC",

    /* ===== Image Table ===== */
    "--table-bg": "#FFFFFF",
    "--table-header-bg": "#F1F5F9",
    "--table-header-text": "#001219",
    "--table-header-border": "#94D2BD",
    "--table-row-bg": "#FFFFFF",
    "--table-row-hover-bg": "#ECF7F5",
    "--table-row-selected-bg": "#D1FAF5",
    "--table-text": "#001219",
    "--table-border": "#94D2BD",
    "--table-divider": "#E2E8F0",
    "--table-text-highlight": "#0A9396",

    /* ===== Table Meta ===== */
    "--table-meta-bg": "#F8FAFC",
    "--table-meta-text": "#0F172A",
    "--table-meta-border": "#CBD5E1",
  },

  nordicDark: {
    /* ===== App Shell ===== */
    "--app-bg": "#000E14",
    "--app-text": "#E5E7EB",
    "--brand-gradient-from": "#004E64",
    "--brand-gradient-to": "#0A6E75",
    "--brand-shadow": "0 8px 22px rgba(0, 78, 100, 0.45)",
    "--brand-text": "#FFFFFF",
    "--brand-badge-bg": "#001A22",
    "--brand-badge-text": "#8EDBD2",
    "--menu-button-text": "#E5E7EB",
    "--menu-button-hover": "rgba(255,255,255,0.05)",

    /* ===== Base Page ===== */
    "--page-bg": "#000E14",
    "--page-surface-bg": "#001A22",
    "--page-surface-border": "#002F3A",
    "--page-surface-shadow": "0 2px 8px rgba(0, 0, 0, 0.55)",
    "--page-title": "#E5E7EB",
    "--page-text": "#9CA3AF",

    /* ===== Project Header ===== */
    "--header-bg": "#000E14",
    "--header-border": "#001A22",
    "--header-text": "#E5E7EB",
    "--header-icon": "#8EDBD2",
    "--header-icon-hover": "rgba(142,219,210,0.16)",
    "--header-menu-bg": "#001A22",
    "--header-menu-border": "#002F3A",
    "--header-menu-item-hover": "#002F3A",
    "--header-divider": "#002F3A",

    /* ===== Project Dash Card ===== */
    "--card-gradient-from": "#0A6E75",
    "--card-gradient-to": "#004E64",
    "--card-text": "#FFFFFF",
    "--card-subtext": "#CFEFED",
    "--card-glass-bg": "rgba(255,255,255,0.07)",
    "--card-glass-border": "rgba(255,255,255,0.14)",
    "--card-bubble": "rgba(255,255,255,0.08)",

    /* ===== Model Dash Card ===== */
    "--model-gradient-from": "#004E64",
    "--model-gradient-to": "#002F3A",
    "--model-text": "#FFFFFF",
    "--model-subtext": "#DDF5F2",
    "--model-glass-bg": "rgba(255,255,255,0.08)",
    "--model-glass-border": "rgba(255,255,255,0.15)",
    "--model-progress-track": "rgba(255,255,255,0.24)",
    "--model-progress-bar": "#8EDBD2",
    "--model-bubble": "rgba(255,255,255,0.1)",

    /* ===== Card Component ===== */
    "--card-bg": "#001A22",
    "--card-border": "#002F3A",
    "--card-icon-bg": "#002F3A",
    "--card-icon": "#8EDBD2",
    "--card-title": "#E5E7EB",
    "--card-action-hover": "#002F3A",
    "--card-action-icon": "#9CA3AF",
    "--card-hover-bg": "#001A22",
    "--card-hover-border": "#0A6E75",
    "--card-hover-shadow": "0 10px 24px rgba(0, 0, 0, 0.6)",

    /* ===== Custom Button ===== */
    "--contained-bg": "#0A6E75",
    "--contained-text": "#FFFFFF",
    "--contained-bg-hover": "#005866",
    "--contained-bg-active": "#00404D",
    "--contained-focus-ring": "rgba(142,219,210,0.4)",
    "--contained-disabled-bg": "#002F3A",
    "--contained-disabled-text": "#7FBEB7",

    "--outlined-bg": "#000E14",
    "--outlined-border": "#002F3A",
    "--outlined-text": "#8EDBD2",
    "--outlined-bg-hover": "#001A22",
    "--outlined-text-hover": "#FFFFFF",
    "--outlined-bg-active": "#002F3A",
    "--outlined-focus-ring": "rgba(142,219,210,0.28)",
    "--outlined-disabled-bg": "#000E14",
    "--outlined-disabled-text": "#5FA7A0",

    /* ===== Badge ===== */
    "--badge-analysis-bg": "rgba(7,89,133,0.35)",
    "--badge-analysis-text": "#7DD3FC",
    "--badge-analysis-border": "#075985",
    "--badge-inspection-bg": "rgba(15,118,110,0.35)",
    "--badge-inspection-text": "#5EEAD4",
    "--badge-inspection-border": "#0F766E",
    "--badge-classification-bg": "rgba(21,94,117,0.35)",
    "--badge-classification-text": "#67E8F9",
    "--badge-classification-border": "#155E75",

    /* ===== Image Table ===== */
    "--table-bg": "#012A36",
    "--table-header-bg": "#001219",
    "--table-header-text": "#E5E7EB",
    "--table-header-border": "#003844",
    "--table-row-bg": "#012A36",
    "--table-row-hover-bg": "#003844",
    "--table-row-selected-bg": "#005F73",
    "--table-text": "#E5E7EB",
    "--table-border": "#003844",
    "--table-divider": "#003844",
    "--table-text-highlight": "#5EEAD4",

    /* ===== Table Meta ===== */
    "--table-meta-bg": "#020617",
    "--table-meta-text": "#E2E8F0",
    "--table-meta-border": "#1E293B",
  },

  neoDark: {
    /* ===== App Shell ===== */
    "--app-bg": "#0B1120",
    "--app-text": "#E5E7EB",
    "--brand-gradient-from": "#5B5EF5",
    "--brand-gradient-to": "#38BDF8",
    "--brand-shadow": "0 10px 28px rgba(91,94,245,0.35)",
    "--brand-text": "#FFFFFF",
    "--brand-badge-bg": "#111827",
    "--brand-badge-text": "#A5B4FC",
    "--menu-button-text": "#E5E7EB",
    "--menu-button-hover": "rgba(255,255,255,0.05)",

    /* ===== Base Page ===== */
    "--page-bg": "#0B1120",
    "--page-surface-bg": "#111827",
    "--page-surface-border": "#1F2937",
    "--page-surface-shadow": "0 2px 10px rgba(0, 0, 0, 0.6)",
    "--page-title": "#E5E7EB",
    "--page-text": "#9CA3AF",

    /* ===== Project Header ===== */
    "--header-bg": "#0B1120",
    "--header-border": "#111827",
    "--header-text": "#E5E7EB",
    "--header-icon": "#A5B4FC",
    "--header-icon-hover": "rgba(165,180,252,0.18)",
    "--header-menu-bg": "#111827",
    "--header-menu-border": "#1F2937",
    "--header-menu-item-hover": "#1F2937",
    "--header-divider": "#1F2937",

    /* ===== Project Dash Card ===== */
    "--card-gradient-from": "#5B5EF5",
    "--card-gradient-to": "#818CF8",
    "--card-text": "#FFFFFF",
    "--card-subtext": "#E0E7FF",
    "--card-glass-bg": "rgba(255,255,255,0.07)",
    "--card-glass-border": "rgba(255,255,255,0.14)",
    "--card-bubble": "rgba(255,255,255,0.07)",

    /* ===== Model Dash Card ===== */
    "--model-gradient-from": "#38BDF8",
    "--model-gradient-to": "#0EA5E9",
    "--model-text": "#FFFFFF",
    "--model-subtext": "#CFFAFE",
    "--model-glass-bg": "rgba(255,255,255,0.07)",
    "--model-glass-border": "rgba(255,255,255,0.14)",
    "--model-progress-track": "rgba(255,255,255,0.22)",
    "--model-progress-bar": "#FFFFFF",
    "--model-bubble": "rgba(255,255,255,0.08)",

    /* ===== Card Component ===== */
    "--card-bg": "#111827",
    "--card-border": "#1F2937",
    "--card-icon-bg": "#1E1B4B",
    "--card-icon": "#A5B4FC",
    "--card-title": "#E5E7EB",
    "--card-action-hover": "#1F2937",
    "--card-action-icon": "#C7D2FE",
    "--card-hover-bg": "#111827",
    "--card-hover-border": "#5B5EF5",
    "--card-hover-shadow": "0 12px 28px rgba(91, 94, 245, 0.38)",

    /* ===== Custom Button ===== */
    "--contained-bg": "#5B5EF5",
    "--contained-text": "#FFFFFF",
    "--contained-bg-hover": "#4F46E5",
    "--contained-bg-active": "#4338CA",
    "--contained-focus-ring": "rgba(91,94,245,0.45)",
    "--contained-disabled-bg": "#111827",
    "--contained-disabled-text": "#6B7280",

    "--outlined-bg": "#0B1120",
    "--outlined-border": "#1F2937",
    "--outlined-text": "#A5B4FC",
    "--outlined-bg-hover": "#111827",
    "--outlined-text-hover": "#E0E7FF",
    "--outlined-bg-active": "#1F2937",
    "--outlined-focus-ring": "rgba(91,94,245,0.3)",
    "--outlined-disabled-bg": "#0B1120",
    "--outlined-disabled-text": "#6B7280",

    /* ===== Badge ===== */
    "--badge-analysis-bg": "rgba(99,102,241,0.3)",
    "--badge-analysis-text": "#A5B4FC",
    "--badge-analysis-border": "#6366F1",
    "--badge-inspection-bg": "rgba(16,185,129,0.3)",
    "--badge-inspection-text": "#6EE7B7",
    "--badge-inspection-border": "#10B981",
    "--badge-classification-bg": "rgba(217,70,239,0.3)",
    "--badge-classification-text": "#F0ABFC",
    "--badge-classification-border": "#D946EF",

    /* ===== Image Table ===== */
    "--table-bg": "#1E293B",
    "--table-header-bg": "#0F172A",
    "--table-header-text": "#E5E7EB",
    "--table-header-border": "#334155",
    "--table-row-bg": "#1E293B",
    "--table-row-hover-bg": "#334155",
    "--table-row-selected-bg": "#6366F1",
    "--table-text": "#E5E7EB",
    "--table-border": "#334155",
    "--table-divider": "#334155",
    "--table-text-highlight": "#A5B4FC",

    /* ===== Table Meta ===== */
    "--table-meta-bg": "#09090B",
    "--table-meta-text": "#D4D4D8",
    "--table-meta-border": "#27272A",
  },
};
